import React from 'react';
import { compose } from 'redux';
import { omit } from 'lodash';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { injectIntl, WrappedComponentProps } from 'react-intl';

import { State as AppState } from 'types';
import { SidebarView, isMobile, SELECTOR_STUB } from 'app-constants';
import { withStreamModule, WithStreamModuleTypes } from 'hoc';
import ui from 'modules/ui';
import promo from 'modules/promo';
import globalMsgs from 'messages';
import Sidebar from './Sidebar';

const {
    actions: { changeSidebarView },
    selectors: { selectSidebarView },
} = ui;
const {
    selectors: { selectAreSportsAvailable },
} = promo;

interface OwnProps {
    isAuthorized: boolean;
    extraTab?: JSX.Element;
}

interface MappedProps {
    activeSidebarView: ReturnType<typeof selectSidebarView>;
    matchIdFullScreen: string | null;
    areSportsAvailable: ReturnType<typeof selectAreSportsAvailable>;
}

interface MappedActions {
    changeSidebarView: typeof changeSidebarView;
}

interface ConnectOwnProps
    extends OwnProps,
        WithStreamModuleTypes.WithStreamModule {}

export type Props = OwnProps &
    MappedProps &
    MappedActions &
    WrappedComponentProps;

type TabsData = { [key in SidebarView]: string };

export class SidebarContainer extends React.Component<Props> {
    tabsData: TabsData = {
        [SidebarView.betslip]: this.props.intl.formatMessage(
            globalMsgs.bettingSlip
        ),
        [SidebarView.bets]: this.props.intl.formatMessage(globalMsgs.myBets),
        [SidebarView.promo]: this.props.intl.formatMessage(globalMsgs.promo),
        [SidebarView.live]: this.props.intl.formatMessage(globalMsgs.live),
    };

    hiddenWithoutAuth: [SidebarView.bets] = [SidebarView.bets];

    hiddenOnMobile: [SidebarView.promo] = [SidebarView.promo];

    hiddenLiveStreamTabs: { isOpen: SidebarView; isClosed: SidebarView } = {
        isOpen: SidebarView.promo,
        isClosed: SidebarView.live,
    };

    changeActiveTabHandler = (sidebarView: SidebarView) =>
        this.props.changeSidebarView({ sidebarView });

    filterTabsData = (
        isAuthorized: boolean,
        isLiveStreamFullscreen: boolean
    ): Partial<TabsData> => {
        let result: Partial<TabsData> = this.tabsData;

        if (!isAuthorized) {
            result = omit(result, this.hiddenWithoutAuth);
        }

        if (isMobile) {
            result = omit(result, this.hiddenOnMobile);
        }

        return omit(
            result,
            isLiveStreamFullscreen
                ? this.hiddenLiveStreamTabs.isOpen
                : this.hiddenLiveStreamTabs.isClosed
        );
    };

    render() {
        const {
            isAuthorized,
            matchIdFullScreen,
            extraTab,
            activeSidebarView,
            areSportsAvailable,
        } = this.props;

        const tabsData = this.filterTabsData(isAuthorized, !!matchIdFullScreen);

        return React.createElement(Sidebar, {
            matchIdFullScreen,
            activeSidebarView,
            tabsData,
            extraTab,
            changeActiveTabHandler: this.changeActiveTabHandler,
            areSportsAvailable,
        });
    }
}

const makeMapStateToProps = (
    state: AppState,
    ownProps: ConnectOwnProps
): MappedProps => {
    const selectMatchIdFullScreen =
        ownProps.streamModule?.selectors.selectMatchIdFullScreen ??
        SELECTOR_STUB;

    const activeSidebarView = selectSidebarView(state);
    const matchIdFullScreen = selectMatchIdFullScreen(state);
    const areSportsAvailable = selectAreSportsAvailable(state);

    return {
        activeSidebarView,
        matchIdFullScreen,
        areSportsAvailable,
    };
};

const mapDispatchToProps: MappedActions = {
    changeSidebarView,
};

export default compose<React.ComponentClass<OwnProps>>(
    injectIntl,
    withRouter,
    withStreamModule,
    connect<MappedProps, MappedActions, ConnectOwnProps>(
        makeMapStateToProps,
        mapDispatchToProps
    )
)(SidebarContainer);
